import { employeeApi, type AttachEmployeeInput } from '@noor/eats-api';
import { http } from '@noor/api';
import { env } from '@noor/config';
import { toUser } from '../lib/map-user';
import type { User, UserRole, Users } from '../model/schema';

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null;

/**
 * The staff roster, wired to the Merchant Employee Service
 * (`openapi-merchant-profile.json`, "Noor — Merchant Profile (merchant)",
 * v1.00.294 — the MERCHANT contour, not the admin one).
 *
 * The write surface is narrower than the Figma flow implies, and every gap
 * below is the backend's rather than an omission here:
 *
 * - **Create is an ATTACH.** The identity (phone, role, password) belongs to
 *   auth-service; this endpoint only binds one to the brand. Attaching by
 *   phone creates the identity if absent, which is what the create form does.
 *   It is IDEMPOTENT: re-attaching an existing member returns their row.
 * - **Update renames, and nothing else.** Phone and role live in auth and are
 *   not editable from here, so the edit form collects names only.
 * - **Delete is a DETACH** — `DELETE merchant-employees/{id}` drops the row
 *   physically. It is the ONLY removal: the merchant contour has no branch
 *   sub-resource, so there is no narrower "revoke one branch" alternative.
 */

/** What the create form collects — an attach-by-phone. */
export interface UserInput {
  firstName: string;
  lastName: string;
  /** E.164, `+`-prefixed (what `FormField.Phone` stores). */
  phone: string;
  /** Only the assignable roles reach here; `merchant-admin` is not offered. */
  role: Exclude<UserRole, 'merchant-admin' | 'unknown'>;
}

/** What a rename collects — the only fields `PATCH` accepts. */
export interface UserRenameInput {
  firstName: string;
  lastName: string;
}

export const userApi = {
  async locations(): Promise<{ id: number; name: string }[]> {
    const raw = await http.get<unknown>('/api/v1/vendors/locations', {
      baseURL: env.VITE_CORE_API_URL,
    });
    const value =
      typeof raw === 'object' && raw !== null && 'data' in raw ? raw.data : raw;
    return Array.isArray(value)
      ? value.filter(isRecord).map((item) => ({
          id: Number(item.id),
          name: String(item.name ?? item.title ?? item.id),
        }))
      : [];
  },
  async employeeLocations(id: string): Promise<number[]> {
    const raw = await http.get<unknown>(
      `/api/v1/vendors/employees/${encodeURIComponent(id)}/locations`,
      { baseURL: env.VITE_CORE_API_URL },
    );
    const value =
      typeof raw === 'object' && raw !== null && 'data' in raw ? raw.data : raw;
    const ids =
      typeof value === 'object' && value !== null && 'vendor_location_ids' in value
        ? value.vendor_location_ids
        : value;
    return Array.isArray(ids) ? ids.map(Number) : [];
  },
  updateEmployeeLocations: (id: string, ids: number[]) =>
    http.put(
      `/api/v1/vendors/employees/${encodeURIComponent(id)}/locations`,
      { vendor_location_ids: ids },
      { baseURL: env.VITE_CORE_API_URL },
    ),
  /**
   * The whole roster.
   *
   * No branch filter is possible — `GET /merchant-employees` takes pagination
   * only, and the gateway rejects unknown query params rather than ignoring
   * them. That matches the screen's need anyway: it lists every member of the
   * merchant, including those with no branch.
   */
  async list(options?: { signal?: AbortSignal }): Promise<Users> {
    const result = await employeeApi.list(undefined, options);
    return { items: result.list.map(toUser) };
  },

  async get(id: User['id'], options?: { signal?: AbortSignal }): Promise<User> {
    return toUser(await employeeApi.get(id, options));
  },

  /**
   * Attach a staff member by phone, creating the identity if it does not exist.
   *
   * Names ride along on the same request so a member is created and named in
   * one call rather than an attach followed by a rename.
   */
  async create(input: UserInput): Promise<User> {
    const payload: AttachEmployeeInput = {
      phone: input.phone,
      role: input.role,
      firstName: input.firstName,
      lastName: input.lastName,
    };
    const { employee } = await employeeApi.attach(payload);
    return toUser(employee);
  },

  /** Rename a staff member (the only editable fields). */
  async update(id: User['id'], input: UserRenameInput): Promise<User> {
    return toUser(await employeeApi.update(id, input));
  },

  /**
   * Detach a staff member from the merchant — what «Удалить сотрудника» calls.
   *
   * The row goes physically, so the member really does leave the roster. No
   * branch id is involved: the delete addresses the employee itself, and the
   * backend reads the brand off that row rather than trusting one from the
   * caller.
   */
  async remove(id: User['id']): Promise<void> {
    await employeeApi.detach(id);
  },
};
